import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PAGE_TEXT } from '../lang';
import { type ProfessionalQualificationFormValues } from './types';
import { ProfessionalQualificationForm } from './ProfessionalQualificationForm';
import {
  addApplicationItem,
  updateApplicationItem,
  useGetProfessionalQualificationFormData,
} from '../../../graphql';
import {
  Alert,
  type AlertDetails,
  FILE_MANAGE_ORDER,
  FilesPanel,
  Loading,
  type FileData,
} from '../../../../../components';
import { handleFileRename } from '../../../../../utils/handleFileRename';

export type ProfessionalQualificationContentProps = {
  applicationNo: string;
  professionalQualificationId?: string;
  enableEdit: boolean;
  enableDelete: boolean;
  index: number;
  onDelete: (e: React.MouseEvent) => void;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

export const ProfessionalQualificationContent: React.FunctionComponent<
  ProfessionalQualificationContentProps
> = props => {
  const { applicationId } = useParams();
  const {
    applicationNo = '',
    professionalQualificationId: initialProfessionalQualificationId = '',
    index: professionalQualificationIndex,
    enableEdit,
    refetchApplication,
    onDelete,
    handleLoading,
    enableDelete,
  } = props;

  let initialValues: ProfessionalQualificationFormValues = {
    awardDate: '',
    awardName: '',
    country: '',
    professionField: '',
    professionalQualification: '',
    awardBodyName: '',
    otherAwardBodyName: '',
    attainmentMeans: '',
  };

  const {
    OTHER_INFORMATION: { PROFESSIONAL_QUALIFICATION },
  } = PAGE_TEXT;

  const [professionalQualificationId, setProfessionalQualificationId] =
    useState(initialProfessionalQualificationId);

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const [supportingDocuments, setSupportingDocuments] = useState<FileData[]>(
    [],
  );

  const isCreateMode = !professionalQualificationId;

  const createProfessionalQualification = addApplicationItem(
    'professionalQualifications',
    refetchApplication,
  );
  const updateProfessionalQualification = updateApplicationItem();

  const { professionalQualificationFormData, countryList, loading, error } =
    useGetProfessionalQualificationFormData(professionalQualificationId);

  const { supportingDocuments: initialSupportingDocuments, ...others } =
    professionalQualificationFormData;

  if (!loading) {
    initialValues = { ...others };
  }

  useEffect(() => {
    setSupportingDocuments(initialSupportingDocuments ?? []);
  }, [loading]);

  const handleSubmit = async (
    values: ProfessionalQualificationFormValues,
  ): Promise<void> => {
    if (supportingDocuments.length < 1) {
      setAlertDetails({
        alertContent: 'Please upload at least one supporting document.',
        alertType: 'warning',
      });
      setOpenAlert(true);
      return;
    }
    const inputSupportingDocuments: FileData[] = supportingDocuments.map(
      (doc, index) => {
        const { name, ...other } = doc;
        return {
          name: handleFileRename(
            applicationNo,
            'professional_qualification',
            index + 1,
          ),
          ...other,
        };
      },
    );
    await handleLoading(async () => {
      try {
        if (isCreateMode) {
          const res = await createProfessionalQualification({
            variables: {
              id: applicationId,
              input: {
                professionalQualification: {
                  ...values,
                  supportingDocuments: inputSupportingDocuments,
                },
              },
            },
          });
          const resProfessionalQualificationId =
            res.data?.addApplicationItem.professionalQualifications[props.index]
              ._id;
          setProfessionalQualificationId(resProfessionalQualificationId);
        } else {
          await updateProfessionalQualification({
            variables: {
              id: applicationId,
              updateId: professionalQualificationId,
              input: {
                professionalQualification: {
                  ...values,
                  supportingDocuments: inputSupportingDocuments,
                },
              },
            },
          });
        }
        alertDetailDef = {
          alertContent: 'Saved Professional Qualification Successfully.',
          alertType: 'success',
        };

        /* eslint-disable */
      } catch (error) {
        alertDetailDef = {
          alertContent: `Saved Professional Qualification Failed.${error}`,
          alertType: 'error',
        };
      } finally {
        setAlertDetails(alertDetailDef);
      }
    });
    setOpenAlert(true);
  };

  const handleFilesChange = (order: number, id?: string): boolean => {
    let newSupportingDocuments = [...supportingDocuments];
    switch (order) {
      case FILE_MANAGE_ORDER.REMOVE:
        // suppose document id is unique
        newSupportingDocuments = newSupportingDocuments.filter(file => {
          return file.id !== id;
        });
        break;
      case FILE_MANAGE_ORDER.UPLOAD:
        if (id) {
          newSupportingDocuments = [
            ...newSupportingDocuments,
            {
              id,
              name: '',
              link: '',
            },
          ];
        }
        break;
      default:
    }
    setSupportingDocuments(newSupportingDocuments);
    return true;
  };

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <div style={{ marginTop: '35px', display: 'flex' }}>
      {loading ? (
        <Loading />
      ) : (
        <React.Fragment>
          <div style={{ flex: 1 }}>
            <ProfessionalQualificationForm
              initialValues={initialValues}
              enableEdit={enableEdit}
              countryList={countryList}
              professionalQualificationIndex={professionalQualificationIndex}
              onDelete={onDelete}
              onSubmit={handleSubmit}
              enableDelete={enableDelete}
            />
            <Alert
              openAlert={openAlert}
              setOpenAlert={setOpenAlert}
              alertDetails={alertDetails}
            />
          </div>
          <div style={{ paddingTop: '15px', marginLeft: '1rem' }}>
            <FilesPanel
              disable={!enableEdit}
              disableDeleteOnly={!enableDelete}
              title={PROFESSIONAL_QUALIFICATION.FILES_PANEL.PANEL_TITLE}
              caption={`${PROFESSIONAL_QUALIFICATION.FILES_PANEL.PANEL_CAPTION}`}
              handleFilesChange={handleFilesChange}
              files={supportingDocuments}
              handleLoading={handleLoading}
              maxFileNumber={5}
            />
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
